import { useLayoutEffect, useRef, useState } from 'react'
import { Link as RouterLink, NavLink, Outlet, useLocation } from 'react-router-dom'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Drawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Divider from '@mui/material/Divider'
import MuiLink from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Link from '@mui/material/Link'
import Tooltip from '@mui/material/Tooltip'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import MenuIcon from '@mui/icons-material/Menu'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import PhoneIcon from '@mui/icons-material/Phone'
import EmailIcon from '@mui/icons-material/Email'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

import { LanguageSwitcher } from './LanguageSwitcher'
import { useLocale } from '../../i18n/LocaleProvider'
import { ScrollButtons } from './ScrollButtons'
import { WhatsAppButton } from './WhatsAppButton'
import { Breadcrumbs, BreadcrumbTitleProvider } from './Breadcrumbs'

/**
 * Lena's number, in the two forms it has to exist in: the one a person reads and the one
 * wa.me and tel: require. Kept together so they cannot drift apart.
 */
const PHONE_DISPLAY = '+358 44 092 26 51'
const PHONE_E164 = '358440922651'
const WHATSAPP_GREEN = '#25D366'
const OWNER_EMAIL = 'lomasaaret@mail.ru'

/**
 * The menu, regrouped.
 *
 * Eight top-level items fitted in English and in nothing else. Measured in Russian the row
 * wanted 985 px against the 707 px the bar can give it, so the whole menu collapsed into the
 * hamburger on a 1280 px laptop - a desktop site with no visible navigation.
 *
 * Five destinations of their own, and the three "what else is there" pages behind one button.
 * Explore, Activities and Helsinki hotels are all answers to the same question, and grouping
 * them is what the page would have said if it had been designed for seven languages from the
 * start rather than for English.
 */
const NAV_ITEMS = [
  { to: '/houses', key: 'nav.houses' },
  { to: '/services', key: 'nav.services' },
  { to: '/gallery', key: 'nav.gallery' },
  { to: '/about', key: 'nav.about' },
  { to: '/contact', key: 'nav.contact' },
] as const

/** Behind one button in the bar; listed flat in the drawer, where there is room. */
const AROUND_ITEMS = [
  { to: '/explore', key: 'nav.explore' },
  { to: '/activities', key: 'nav.activities' },
  { to: '/hotels', key: 'nav.hotels' },
] as const

/** Every link, in reading order, for the drawer and the footer. */
const ALL_NAV_ITEMS = [
  NAV_ITEMS[0],
  ...AROUND_ITEMS,
  NAV_ITEMS[1],
  NAV_ITEMS[2],
  NAV_ITEMS[3],
  NAV_ITEMS[4],
] as const

/**
 * The public shell: a translucent app bar, a drawer on small screens, and a footer.
 *
 * Everything positional uses logical CSS properties (`insetInlineStart`, `ms`, `me`) rather
 * than left/right. Emotion's RTL plugin flips physical properties automatically, but logical
 * ones need no flipping at all, which means one fewer thing that can go wrong in Hebrew.
 */
export function PublicLayout() {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [aroundAnchor, setAroundAnchor] = useState<HTMLElement | null>(null)
  const location = useLocation()
  const { locale } = useLocale()

  /**
   * Does the full menu actually fit?
   *
   * Measured, not guessed from a breakpoint. The menu is eight items plus a language, a
   * WhatsApp button and a call to action, and the labels are a different length in every one
   * of seven languages: in Russian at 1024 px the bar wanted 1181 px and the booking button
   * was simply off the side of the screen. A breakpoint that works for English is wrong for
   * Russian and German, and tuning one per language is a list nobody will maintain.
   *
   * The nav stays mounted when it does not fit - hidden, not unmounted - so it can still be
   * measured. Unmounting it would make the toolbar fit again, which would remount it, which
   * would make it overflow again.
   */
  const railRef = useRef<HTMLDivElement>(null)
  const navRef = useRef<HTMLDivElement>(null)
  const [navFits, setNavFits] = useState(true)

  useLayoutEffect(() => {
    const measure = () => {
      const rail = railRef.current
      const nav = navRef.current
      if (!rail || !nav) return
      // A pixel of slack: sub-pixel layout rounding otherwise flips this on and off forever.
      setNavFits(nav.scrollWidth <= rail.clientWidth + 1)
    }
    measure()
    const observer = new ResizeObserver(measure)
    if (railRef.current) observer.observe(railRef.current)
    if (navRef.current) observer.observe(navRef.current)
    return () => observer.disconnect()
    // Re-measured on language change: the words change length, so the answer changes.
  }, [locale])

  const showFullNav = !isMobile && navFits

  return (
    <Box sx={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="sticky">
        <Container>
          <Toolbar disableGutters sx={{ gap: 1, minHeight: { xs: 60, md: 72 } }}>
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                textDecoration: 'none',
                color: 'inherit',
                // Never shrinks and never pushes. The rail beside it takes the slack.
                flexShrink: 0,
              }}
            >
              <Box aria-hidden sx={{ fontSize: '1.5rem', lineHeight: 1 }}>
                🛶
              </Box>
              <Box>
                <Typography
                  component="span"
                  sx={{
                    display: 'block',
                    fontFamily: theme.typography.h3.fontFamily,
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    lineHeight: 1.1,
                  }}
                >
                  Lomasaaret
                </Typography>
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ display: { xs: 'none', sm: 'block' }, color: 'text.secondary', lineHeight: 1 }}
                >
                  Kerimäki · Finland
                </Typography>
              </Box>
            </Box>

            {/* The rail takes whatever space is left over and clips rather than pushes, so
                the language, WhatsApp and the booking button can never be shoved off the
                screen however long the labels get. */}
            <Box
              ref={railRef}
              sx={{
                flex: '1 1 auto',
                minWidth: 0,
                overflow: 'hidden',
                display: isMobile ? 'none' : 'flex',
                justifyContent: 'center',
              }}
            >
              <Stack
                ref={navRef}
                direction="row"
                spacing={0.5}
                aria-hidden={!showFullNav}
                sx={{
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  // Hidden, not unmounted: it has to stay measurable, or hiding it would make
                  // the bar fit, which would show it again, which would overflow again.
                  visibility: showFullNav ? 'visible' : 'hidden',
                  pointerEvents: showFullNav ? 'auto' : 'none',
                }}
              >
                <Button
                  component={NavLink}
                  to={NAV_ITEMS[0].to}
                  color="inherit"
                  tabIndex={showFullNav ? undefined : -1}
                  sx={{
                    flexShrink: 0,
                    fontWeight: 500,
                    color: location.pathname.startsWith(NAV_ITEMS[0].to)
                      ? 'primary.main'
                      : 'text.primary',
                  }}
                >
                  {t(NAV_ITEMS[0].key)}
                </Button>

                <Button
                  color="inherit"
                  tabIndex={showFullNav ? undefined : -1}
                  endIcon={<ExpandMoreIcon />}
                  onClick={(event) => setAroundAnchor(event.currentTarget)}
                  sx={{
                    flexShrink: 0,
                    fontWeight: 500,
                    color: AROUND_ITEMS.some((one) => location.pathname.startsWith(one.to))
                      ? 'primary.main'
                      : 'text.primary',
                  }}
                >
                  {t('nav.aroundHere')}
                </Button>
                <Menu
                  anchorEl={aroundAnchor}
                  open={Boolean(aroundAnchor)}
                  onClose={() => setAroundAnchor(null)}
                >
                  {AROUND_ITEMS.map((item) => (
                    <MenuItem
                      key={item.to}
                      component={RouterLink}
                      to={item.to}
                      onClick={() => setAroundAnchor(null)}
                      selected={location.pathname.startsWith(item.to)}
                    >
                      {t(item.key)}
                    </MenuItem>
                  ))}
                </Menu>

                {NAV_ITEMS.slice(1).map((item) => (
                  <Button
                    key={item.to}
                    component={NavLink}
                    to={item.to}
                    color="inherit"
                    tabIndex={showFullNav ? undefined : -1}
                    sx={{
                      flexShrink: 0,
                      fontWeight: 500,
                      color: location.pathname.startsWith(item.to) ? 'primary.main' : 'text.primary',
                    }}
                  >
                    {t(item.key)}
                  </Button>
                ))}
              </Stack>
            </Box>

            {/* Always the flag alone, never the word.
                It used to widen itself when the menu fitted, which made the menu stop fitting,
                which narrowed it again - the switcher's width was an input to the measurement
                that decided its width. Fixing it at one width breaks the loop, and the footer
                spells all seven languages out in their own scripts anyway. */}
            <LanguageSwitcher compact />

            {/* On every page, not buried on the contact page. Most enquiries for a place like
                this arrive on WhatsApp, and a guest deciding at 11pm should not have to hunt
                for the number. It is a wa.me link, so it opens their own WhatsApp and needs
                nothing on the server. */}
            <Tooltip title={t('nav.whatsappUs')}>
              <IconButton
                component="a"
                href={'https://wa.me/' + PHONE_E164}
                target="_blank"
                rel="noopener"
                aria-label={t('nav.whatsappUs')}
                sx={{ color: WHATSAPP_GREEN }}
              >
                <WhatsAppIcon />
              </IconButton>
            </Tooltip>

            {!isMobile && (
              <Button
                component={RouterLink}
                to="/houses"
                variant="contained"
                color="primary"
                sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                {t('nav.bookNow')}
              </Button>
            )}

            {/* The drawer is now the menu whenever the full one does not fit, not only on a
                phone. Same drawer, same items. */}
            {!showFullNav && (
              <IconButton
                edge="end"
                onClick={() => setDrawerOpen(true)}
                aria-label={t('nav.menu')}
                color="inherit"
                sx={{ flexShrink: 0 }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        // MUI's Drawer takes physical anchors only, so the theme direction is applied here
        // explicitly. The drawer must open from the edge the menu button sits on.
        anchor={theme.direction === 'rtl' ? 'left' : 'right'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        slotProps={{ paper: { sx: { width: 290 } } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={() => setDrawerOpen(false)} aria-label={t('common.close')}>
            <CloseIcon />
          </IconButton>
        </Box>
        <List>
          {ALL_NAV_ITEMS.map((item) => (
            <ListItemButton
              key={item.to}
              component={RouterLink}
              to={item.to}
              onClick={() => setDrawerOpen(false)}
            >
              <ListItemText primary={t(item.key)} />
            </ListItemButton>
          ))}
        </List>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <Button
              component="a"
              href={'https://wa.me/' + PHONE_E164}
              target="_blank"
              rel="noopener"
              startIcon={<WhatsAppIcon />}
              variant="contained"
              fullWidth
              size="large"
              onClick={() => setDrawerOpen(false)}
              sx={{ bgcolor: WHATSAPP_GREEN, '&:hover': { bgcolor: '#1da851' } }}
            >
              {PHONE_DISPLAY}
            </Button>
            <Button
              component={RouterLink}
              to="/houses"
              variant="contained"
              fullWidth
              size="large"
              onClick={() => setDrawerOpen(false)}
            >
              {t('nav.bookNow')}
            </Button>
          </Stack>
        </Box>
      </Drawer>

      {/*
        The provider wraps both the trail and the Outlet, so a detail page deeper in the
        tree can hand its resolved name upward. The trail renders nothing on the home page.
      */}
      <BreadcrumbTitleProvider>
        <Breadcrumbs />
        <Box component="main" sx={{ flex: 1 }}>
          <Outlet />
        </Box>
      </BreadcrumbTitleProvider>

      <SiteFooter />
      <ScrollButtons />
      <WhatsAppButton />
    </Box>
  )
}

function SiteFooter() {
  const { t } = useTranslation()
  const { locale, setLocale, locales } = useLocale()
  const year = new Date().getFullYear()

  return (
    <Box
      component="footer"
      sx={{
        mt: 10,
        pt: 6,
        pb: 4,
        bgcolor: 'primary.dark',
        color: 'common.white',
      }}
    >
      <Container>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 4, md: 8 }}
          sx={{ mb: 5 }}
        >
          <Box sx={{ maxWidth: 340 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>
              🛶 Lomasaaret
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.82 }}>
              {t('footer.tagline')}
            </Typography>
          </Box>

          <Stack direction="row" spacing={{ xs: 4, sm: 8 }} sx={{ flexWrap: 'wrap' }}>
            <FooterColumn
              title={t('footer.explore')}
              links={[
                { to: '/explore', label: t('nav.explore') },
                { to: '/services', label: t('nav.services') },
                { to: '/activities', label: t('nav.activities') },
                { to: '/hotels', label: t('nav.hotels') },
                { to: '/gallery', label: t('nav.gallery') },
                { to: '/about', label: t('nav.about') },
              ]}
            />
            <FooterColumn
              title={t('footer.stay')}
              links={[
                { to: '/houses', label: t('nav.houses') },
                { to: '/booking', label: t('booking.lookupTitle') },
              ]}
            />
            <FooterColumn
              title={t('footer.practical')}
              links={[
                { to: '/contact', label: t('nav.contact') },
                { to: '/admin', label: t('footer.adminLink') },
              ]}
            />

            {/* Not a link list: these are the two ways people actually get in touch, and they
                belong on every page rather than behind one. */}
            <Box>
              <Typography variant="overline" sx={{ opacity: 0.72, display: 'block', mb: 1 }}>
                {t('footer.talkToUs')}
              </Typography>
              <Stack spacing={0.75}>
                <Link
                  href={'https://wa.me/' + PHONE_E164}
                  target="_blank"
                  rel="noopener"
                  color="inherit"
                  variant="body2"
                  underline="hover"
                  sx={{ opacity: 0.9, display: 'flex', alignItems: 'center', gap: 0.75 }}
                >
                  <WhatsAppIcon fontSize="small" sx={{ color: WHATSAPP_GREEN }} />
                  {t('nav.whatsappUs')}
                </Link>
                <Link
                  href={'tel:+' + PHONE_E164}
                  color="inherit"
                  variant="body2"
                  underline="hover"
                  sx={{ opacity: 0.9, display: 'flex', alignItems: 'center', gap: 0.75 }}
                >
                  <PhoneIcon fontSize="small" />
                  {PHONE_DISPLAY}
                </Link>
                <Link
                  href={'mailto:' + OWNER_EMAIL}
                  color="inherit"
                  variant="body2"
                  underline="hover"
                  sx={{ opacity: 0.9, display: 'flex', alignItems: 'center', gap: 0.75 }}
                >
                  <EmailIcon fontSize="small" />
                  {OWNER_EMAIL}
                </Link>
              </Stack>
            </Box>
          </Stack>
        </Stack>

        {/* Every language, written in its own script, on every page. The switcher in the
            header is a flag and a menu; a visitor who does not recognise the flag needs to
            see the word. */}
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.18)', mb: 2.5 }} />

        <Stack direction="row" spacing={2} sx={{ mb: 2.5, flexWrap: 'wrap', rowGap: 1 }}>
          {locales.map((language) => (
            <Link
              key={language.code}
              component="button"
              type="button"
              onClick={() => setLocale(language.code)}
              color="inherit"
              variant="body2"
              underline="hover"
              sx={{
                opacity: language.code === locale ? 1 : 0.75,
                fontWeight: language.code === locale ? 700 : 400,
                background: 'none',
                border: 0,
                cursor: 'pointer',
              }}
            >
              {language.nativeName}
            </Link>
          ))}
        </Stack>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.18)', mb: 2.5 }} />

        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          © {year} Lomasaaret Lakeside Cottages · Joensuuntie 3186, 58200 Kerimäki · +358 44 092 2651 ·{' '}
          {t('footer.rights')}{' · '}
          <MuiLink component={RouterLink} to="/admin" color="inherit" underline="hover">
            {t('nav.ownerConsole')}
          </MuiLink>
        </Typography>
      </Container>
    </Box>
  )
}

function FooterColumn({
  title,
  links,
}: {
  title: string
  links: { to: string; label: string }[]
}) {
  return (
    <Box>
      <Typography variant="overline" sx={{ opacity: 0.72, display: 'block', mb: 1 }}>
        {title}
      </Typography>
      <Stack spacing={0.75}>
        {links.map((link) => (
          <Link
            key={link.to}
            component={RouterLink}
            to={link.to}
            color="inherit"
            variant="body2"
            sx={{ opacity: 0.9 }}
          >
            {link.label}
          </Link>
        ))}
      </Stack>
    </Box>
  )
}
